import { FC, useContext, useEffect, useState } from 'react';

import { useMutation } from '@apollo/client';
import { Button, H2, NumberInput, Paragraph } from '@smartshell/ui-kit';
import cn from 'classnames';

import { LocalizationContext, TLocalesKeys } from 'localization';
import { useNotCompleted } from 'modals/ClientRegisterModal/NotComletedContext';
import {
  IVerifyClientRequest,
  IVerifyClientResponse,
  VERIFY_CLIENT,
} from 'schemas';
import { EVerificationType } from 'types';

import { ICode, IVerifyCodeProps } from './interfases';
import { getDefaultCodes } from './utils';
import s from './Verify.module.scss';

export const VerifyCode: FC<IVerifyCodeProps> = ({
  verifyClientId,
  phone,
  email,
  onPhoneChange,
  setOnSuccess,
  setVerification,
  getClients,
  variables,
  verificationType,
  verification,
}) => {
  const { t } = useContext(LocalizationContext);

  const {
    // removeClientById,
    markClientAsVerified,
  } = useNotCompleted();
  const [
    isSubmitting,
    // setIsSubmitting
  ] = useState(false);
  const [error, setError] = useState<TLocalesKeys | null>(null);

  const [codes, setCodes] = useState<Array<ICode>>(getDefaultCodes());

  const [verifyClient, { loading: verifyLoading }] = useMutation<
    IVerifyClientResponse,
    IVerifyClientRequest
  >(VERIFY_CLIENT, {
    onCompleted: () => {
      setOnSuccess && setOnSuccess(true);
      setVerification && setVerification(null);
      getClients &&
        getClients({
          variables: {
            ...variables,
            input: {
              ...variables?.input,
            },
          },
        });
      markClientAsVerified(verifyClientId as number);
    },
    onError: () => {
      onError();
    },
  });

  const onError = (): void => setError('errors.incorrectCode');

  useEffect(() => {
    codes[0].ref?.focus();
  }, []);

  return (
    <div className={s.container}>
      <H2 className={s.header}>
        {verificationType === EVerificationType.INCOMING_CALL &&
          t('confirmation.incoming_call.description', phone)}
        {verificationType === EVerificationType.TELEGRAM &&
          t('confirmation.telegram.description', phone)}
        {verificationType === EVerificationType.VK &&
          t('confirmation.vk.description', phone)}
        {verificationType === EVerificationType.EMAIL &&
          t('confirmation.email.description', email)}
      </H2>

      <Paragraph bold color="secondary" className={s.description}>
        {t('clientRegister.verify.description')}
      </Paragraph>

      <div className={s.code}>
        {codes.map((code, i) => (
          <NumberInput
            key={`code-${i}`}
            placeholder="•"
            pattern="\d"
            ref={(instance) => (code.ref = instance)}
            className={cn(s.digit, error && s.withError)}
            variant="secondary"
            value={code.value}
            disabled={isSubmitting}
            onChange={(value) => {
              setCodes((codes) =>
                codes.map((prevCode, index) => {
                  if (index === i) {
                    return { ...prevCode, value };
                  }

                  return { ...prevCode };
                })
              );

              if (error) {
                setError(null);
              }

              const nextRef = codes[i + 1]?.ref;
              if (value !== '') {
                if (nextRef) {
                  return nextRef.focus();
                } else {
                  return code.ref?.blur();
                }
              }

              const prevRef = codes[i - 1]?.ref;
              if (value === '' && prevRef) {
                prevRef.focus();
              }
            }}
            bordered
          />
        ))}
      </div>
      {error && <span className={s.error}>{t(error)}</span>}
      <div className={s.controls}>
        <Button
          fullWidth
          isLoading={verifyLoading}
          onClick={() => {
            if (!verification?.verification_id) return;
            const code = codes.map(({ value }) => value).join('');

            return verifyClient({
              variables: {
                input: {
                  code,
                  verification_id: verification.verification_id,
                },
              },
            });
          }}
          disabled={
            codes.some(({ value }) => value === '' || value > 9) || isSubmitting
          }
        >
          {t('confirm')}
        </Button>
        <Button variant="ghost" className={cn(s.link)} onClick={onPhoneChange}>
          {verificationType === EVerificationType.EMAIL
            ? t('confirmation.changeEmail')
            : t('confirmation.changePhone')}
        </Button>
      </div>
    </div>
  );
};
